import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { QUERY_KEY } from '../utils/constants'
import { useSelector } from 'react-redux'
import {
  coinToCredit,
  fetchCoinBalance,
  fetchCreditBalance,
  fetchCreditTransaction,
  fetchCoinTransaction,
  fetchEarnCoins
} from '../api/walletApi'
import { clearApiGetCache } from 'shared-ui/api'

export const useCoinTransaction = (
  { fromDate = '', toDate = '',items=1000,page=1 } = {},
  options = {}
) => {
  return useQuery({
    queryKey: [QUERY_KEY.GetCoinTransaction, fromDate, toDate],
    queryFn: async () => {
      const resp = await fetchCoinTransaction({ fromDate, toDate,items,page })
      return resp
    },
    staleTime: 0,
    refetchOnMount: true, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    placeholderData: Array(5).fill({ id: 'placeholder', amount: 'Loading...' }),
    ...options
  })
}

export const useCreditTransaction = (
  { fromDate = '', toDate = '' } = {},
  options = {}
) => {
  return useQuery({
    queryKey: [QUERY_KEY.GetCreditTransaction, fromDate, toDate],
    queryFn: async () => {
      const resp = await fetchCreditTransaction({ fromDate, toDate })
      return resp
    },
    staleTime: 0,
    refetchOnMount: true, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    placeholderData: Array(5).fill({ id: 'placeholder', amount: 'Loading...' }),
    ...options
  })
}

export const useCreditBalance = (options = {}) => {
  const userId = JSON.parse(localStorage.getItem('user'))?.user_id || ''
  return useQuery({
    queryKey: [QUERY_KEY.GetCreditBalance, userId],
    queryFn: async () => {
      const resp = await fetchCreditBalance()
      return resp
    },
    enabled: !!userId,
    staleTime:0,
    cacheTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options
  })
}
export const useCoinBalance = (options = {}) => {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated)

  const userId = JSON.parse(localStorage.getItem('user')) || ''
  return useQuery({
    queryKey: [QUERY_KEY.GetCoinBalance, userId],
    queryFn: async () => {
      const resp = await fetchCoinBalance()
      return resp
    },
    enabled: isAuthenticated && !!userId,
    staleTime: 30 * 60 * 1000,
    cacheTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: { id: 'placeholder', balance: 'Loading...' },
    ...options
  })
}
export const useCoinToCreditConversion = (options = {}) => {
  const queryClient = useQueryClient()
  const { onSuccess: externalOnSuccess, ...restOptions } = options
  return useMutation({
    mutationKey: ['COIN_CONVERT'],
    mutationFn: async (coins) => {
      const resp = await coinToCredit(coins)
      return resp
    },
    onSuccess: async (...args) => {
      clearApiGetCache()
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY.GetCoinBalance] })
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY.GetCreditBalance] })
      if (externalOnSuccess) {
        await externalOnSuccess(...args)
      }
    },
    ...restOptions
  })
}

export const useEarnMoreCoins = (options = {}) => {
  return useQuery({
    queryKey: [QUERY_KEY.GET_MORE_COIN],
    queryFn: fetchEarnCoins,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
    placeholderData: Array(3).fill({ id: 'placeholder', reward: 'Loading...' }),
    ...options
  })
}
